import { useRolodexEdit } from 'features/fde'
import { BottomOverlayMenuButton } from 'features/overview'
import { usePointCloudContext } from 'features/pointClouds'
import { useRolodexCache } from 'features/rolodex'
import { Hash } from 'lucide-react'
import { getCameraPositionAndLookAt } from 'pages/pointClouds/utils/cameraUtils'
import React, { FC, useCallback } from 'react'
import styled from 'styled-components'

const StyledHashIcon = styled(Hash)`
  width: 32px;
  height: 32px;
  margin-left: -4px;
`

interface AddHotkeyButtonProps {
  closeMenu: () => void
}

export const AddHotkeyButton: FC<AddHotkeyButtonProps> = ({ closeMenu }) => {
  const { rolodexPatch } = useRolodexEdit()
  const { patchEntry } = useRolodexCache()
  const { camera, hotkeys, activePointCloud } = usePointCloudContext()

  const getNextAvailableHotkeySlot = useCallback((): string | null => {
    for (let i = 1; i <= 9; i++) {
      if (!hotkeys?.[i.toString()]) {
        return i.toString()
      }
    }
    return null
  }, [hotkeys])

  const handleAddHotkey = useCallback(async () => {
    const nextSlot = getNextAvailableHotkeySlot()
    if (!nextSlot || !activePointCloud) {
      return
    }

    const cameraData = getCameraPositionAndLookAt(camera.current)
    if (!cameraData) {
      return
    }

    const newHotkeyData = {
      position: cameraData.position,
      lookAt: cameraData.lookAt,
    }

    try {
      // Immediately update the local cache for instant UI updates
      patchEntry({
        id: activePointCloud.id,
        data: {
          hotkeys: {
            ...activePointCloud.data.hotkeys,
            [nextSlot]: newHotkeyData,
          },
        },
      })

      // Also patch the backend
      await rolodexPatch([
        {
          id: activePointCloud.id,
          data: {
            hotkeys: {
              [nextSlot]: newHotkeyData,
            },
          },
        },
      ])

      closeMenu()
    } catch (error) {
      console.error('Failed to save hotkey:', error)
    }
  }, [
    getNextAvailableHotkeySlot,
    camera,
    activePointCloud,
    patchEntry,
    rolodexPatch,
    closeMenu,
  ])

  const nextSlot = getNextAvailableHotkeySlot()
  const isDisabled = !nextSlot

  const handleClick = useCallback(() => {
    if (isDisabled) return
    handleAddHotkey()
  }, [isDisabled, handleAddHotkey])

  if (isDisabled) {
    return null // Don't render the button when all slots are used
  }

  return (
    <BottomOverlayMenuButton onClick={handleClick}>
      <StyledHashIcon />
      Add Hotkey ({nextSlot}) here
    </BottomOverlayMenuButton>
  )
}
